import { Badge, DataTableColumnHeader } from "@/components/ui";
import { StatuteConfirmedIcon } from "@/components/ui/Icons/StatuteConfirmedIcon";
import { formatDate } from "@/lib/utils/format";
import {
  getCalendarDaysUntilStatute,
  getStatuteDaysBadgeVariant,
} from "@/lib/utils/matters/statute-urgency-utils";
import { type ColumnDef } from "@tanstack/react-table";

export type DashboardApproachingStatuteRow = {
  matterId: string;
  matterTitle?: string | null;
  matterStatusLabel?: string | null;
  matterStatuteOfLimitations?: string | null;
  matterStatuteOfLimitationsConfirmed?: boolean | null;
};

const formatDaysLabel = (daysRemaining: number | null) => {
  if (daysRemaining === null) {
    return "-";
  }
  if (daysRemaining < 0) {
    return `${Math.abs(daysRemaining)}d past`;
  }
  if (daysRemaining === 0) {
    return "Today";
  }
  return `In ${daysRemaining}d`;
};

export const getDashboardMyApproachingStatutesColumns =
  (): ColumnDef<DashboardApproachingStatuteRow>[] => [
    {
      accessorFn: (row) => row.matterTitle,
      id: "Matter",
      enableHiding: false,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => row.original.matterTitle ?? "-",
    },
    {
      accessorFn: (row) => row.matterStatusLabel,
      id: "Status",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) =>
        row.original.matterStatusLabel ? (
          <span className="border-border rounded-full border px-2.5 py-0.5 text-xs font-semibold">
            {row.original.matterStatusLabel}
          </span>
        ) : (
          "-"
        ),
    },
    {
      accessorFn: (row) => row.matterStatuteOfLimitations,
      id: "Statute of Limitations",
      sortingFn: "customDateTime",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => {
        const statuteDate = row.original.matterStatuteOfLimitations;
        if (!statuteDate) {
          return "-";
        }

        return (
          <div className="inline-flex items-center gap-2">
            <span>{formatDate(statuteDate)}</span>
            <StatuteConfirmedIcon
              confirmed={row.original.matterStatuteOfLimitationsConfirmed}
            />
          </div>
        );
      },
    },
    {
      accessorFn: (row) =>
        getCalendarDaysUntilStatute(row.matterStatuteOfLimitations),
      id: "Days Remaining",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const daysRemaining = row.getValue() as number | null;
        const content = formatDaysLabel(daysRemaining);

        if (content === "-") {
          return content;
        }

        return (
          <Badge variant={getStatuteDaysBadgeVariant(daysRemaining)}>
            {content}
          </Badge>
        );
      },
    },
  ];
